import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  FileText,
  Receipt,
  Users,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ModernDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [dashboardData, setDashboardData] = useState({
    todaySales: 0,
    monthlySales: 0,
    totalInvoices: 0,
    totalClosedOrders: 0,
    newCustomers: 0,
    recentTransactions: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh flag - disabled by default, can be enabled later
    const AUTO_REFRESH_ENABLED = false; // Set to true to enable auto-refresh
    const AUTO_REFRESH_INTERVAL = 10000; // 10 seconds

    if (AUTO_REFRESH_ENABLED) {
      const interval = setInterval(fetchDashboardData, AUTO_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch invoices, closed orders, and customers for dashboard data
      // Limit to last 100 records for better performance
      const [invoicesResponse, closedOrdersResponse, customersResponse] = await Promise.all([
        axios.get('/sales/invoices?limit=100&sort=-createdAt'),
        axios.get('/sales/closed-orders?limit=100&sort=-createdAt'),
        axios.get('/admin/customers?limit=100')
      ]);

      console.log('📊 Dashboard API Responses:');
      console.log('Invoices Response:', invoicesResponse.data);
      console.log('Closed Orders Response:', closedOrdersResponse.data);
      console.log('Customers Response:', customersResponse.data);

      const invoicesRaw = invoicesResponse.data.invoices || invoicesResponse.data || [];
      const closedOrdersRaw = closedOrdersResponse.data.closedOrders || closedOrdersResponse.data || [];
      const customers = Array.isArray(customersResponse.data) ? customersResponse.data : (customersResponse.data.customers || []);

      // Filter out cancelled transactions for accurate metrics
      const invoices = invoicesRaw.filter(inv => inv.status !== 'cancelled' && inv.cancellation_status !== 'cancelled');
      const closedOrders = closedOrdersRaw.filter(order => order.status !== 'cancelled' && order.cancellation_status !== 'cancelled');

      console.log('📋 Parsed Data (Active):');
      console.log('Invoices count:', invoices.length);
      console.log('Closed Orders count:', closedOrders.length);
      console.log('Customers count:', customers.length);

      // Calculate metrics
      const today = new Date().toDateString();
      const thisMonth = new Date().getMonth();
      const thisYear = new Date().getFullYear();

      // Combine invoices and closed orders for comprehensive metrics
      const allTransactions = [
        ...invoices.map(inv => ({
          ...inv,
          type: 'invoice',
          amount: inv.totalAmount || inv.total_amount || 0,
          date: inv.soldAt || inv.date || inv.created_at
        })),
        ...closedOrders.map(order => ({
          ...order,
          type: 'closed_order',
          amount: order.totalAmount || order.total_amount || 0,
          date: order.soldAt || order.date || order.created_at
        }))
      ];

      const todayTransactions = allTransactions
        .filter(trans => new Date(trans.date).toDateString() === today);
      // Calculate sales as revenue + cost (matching admin dashboard calculation)
      const todaySalesBreakdown = todayTransactions.reduce((acc, trans) => {
        const revenue = trans.amount || 0;
        // Estimate actual cost from items if available
        const cost = (trans.items || []).reduce((itemCost, item) => {
          const itemCostPrice = item.costPrice || 0;
          return itemCost + (itemCostPrice * (item.quantity || 0));
        }, 0);
        return { revenue: acc.revenue + revenue, cost: acc.cost + cost };
      }, { revenue: 0, cost: 0 });
      const todaySales = todaySalesBreakdown.revenue + todaySalesBreakdown.cost;

      console.log('📊 Sales Dashboard - Today Transactions:', todayTransactions);
      console.log('📊 Sales Dashboard - Today Sales Breakdown:', { revenue: todaySalesBreakdown.revenue, cost: todaySalesBreakdown.cost, total: todaySales });

      const monthlyTransactions = allTransactions
        .filter(trans => {
          const transDate = new Date(trans.date);
          return transDate.getMonth() === thisMonth && transDate.getFullYear() === thisYear;
        });
      // Calculate sales as revenue + cost (matching admin dashboard calculation)
      const monthlySalesBreakdown = monthlyTransactions.reduce((acc, trans) => {
        const revenue = trans.amount || 0;
        // Estimate actual cost from items if available
        const cost = (trans.items || []).reduce((itemCost, item) => {
          const itemCostPrice = item.costPrice || 0;
          return itemCost + (itemCostPrice * (item.quantity || 0));
        }, 0);
        return { revenue: acc.revenue + revenue, cost: acc.cost + cost };
      }, { revenue: 0, cost: 0 });
      const monthlySales = monthlySalesBreakdown.revenue + monthlySalesBreakdown.cost;

      console.log('📊 Sales Dashboard - Monthly Transactions:', monthlyTransactions);
      console.log('📊 Sales Dashboard - Monthly Sales Breakdown:', { revenue: monthlySalesBreakdown.revenue, cost: monthlySalesBreakdown.cost, total: monthlySales });

      // Calculate true total counts (using filtered lengths for dashboard consistency)
      const totalInvoices = invoices.length;
      const totalClosedOrders = closedOrders.length;
      const newCustomers = customersResponse.data.pagination?.totalItems || (Array.isArray(customers) ? customers.length : 0);

      // Get recent transactions (invoices and closed orders combined, sorted by date)
      const recentTransactions = allTransactions
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5)
        .map(trans => ({
          id: trans._id || trans.id,
          invoice_number: trans.invoice_number || trans.closed_order_number || trans.order_number || trans.estimate_number || `${trans.type === 'invoice' ? 'INV' : 'CO'}-${trans._id?.slice(-8) || trans.id}`,

          customer_name: trans.customer_name || 'Walk-in Customer',
          date: trans.date || trans.created_at,
          totalAmount: trans.amount,
          payment_status: trans.payment_status || 'pending',
          type: trans.type
        }));

      console.log('💰 Calculated Metrics:');
      console.log('Today Sales:', todaySales);
      console.log('Monthly Sales:', monthlySales);
      console.log('Total Invoices:', totalInvoices);
      console.log('Total Closed Orders:', totalClosedOrders);
      console.log('Total Customers:', newCustomers);
      console.log('Recent Transactions:', recentTransactions);

      setDashboardData({
        todaySales,
        monthlySales,
        totalInvoices,
        totalClosedOrders,
        newCustomers,
        recentTransactions
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set default values on error
      setDashboardData({
        todaySales: 0,
        monthlySales: 0,
        totalInvoices: 0,
        totalClosedOrders: 0,
        newCustomers: 0,
        recentTransactions: []
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'partial':
        return <Clock className="h-4 w-4 text-orange-500" />;
      default:
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return 'Paid';
      case 'partial':
        return 'Partial';
      default:
        return 'Pending';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A57D]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-[#0A5F38]/5 to-[#C5A57D]/10 rounded-2xl p-8 border border-[#0A5F38]/10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A5F38] mb-2">Dashboard Overview</h1>
            <p className="text-lg text-slate-600">
              Welcome to IDealtrendz - Your Interior Design Store Management System
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Track your sales, manage inventory, and grow your business with powerful insights.
            </p>
          </div>
          <div className="hidden lg:block">
            <div className="w-24 h-24 bg-gradient-to-br from-[#0A5F38] to-[#0A5F38]/80 rounded-2xl flex items-center justify-center">
              <TrendingUp className="h-12 w-12 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-5' : 'lg:grid-cols-3'} gap-6`}>
        {isAdmin && (
          <>
            {/* Today's Sales */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#0A5F38]/10 rounded-xl flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-[#0A5F38]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-[#0A5F38] mb-1">
                {formatCurrency(dashboardData.todaySales)}
              </h3>
              <p className="text-slate-600 text-sm">Total Sales Today</p>
            </div>

            {/* Monthly Sales */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[#C5A57D]/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-[#0A5F38]" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-[#0A5F38] mb-1">
                {formatCurrency(dashboardData.monthlySales)}
              </h3>
              <p className="text-slate-600 text-sm">Monthly Sales</p>
            </div>
          </>
        )}

        {/* Total Invoices */}
        <div
          onClick={() => navigate('/sales/invoices')}
          className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-200 cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-[#0A5F38] mb-1">
            {dashboardData.totalInvoices}
          </h3>
          <p className="text-slate-600 text-sm">Total Invoices</p>
        </div>

        {/* Total Closed Orders */}
        <div
          onClick={() => navigate('/sales/closed-orders')}
          className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-200 cursor-pointer"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#C5A57D]/10 rounded-xl flex items-center justify-center">
              <Receipt className="h-6 w-6 text-[#C5A57D]" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-[#0A5F38] mb-1">
            {dashboardData.totalClosedOrders}
          </h3>
          <p className="text-slate-600 text-sm">Total Closed Orders</p>
        </div>

        {/* New Customers */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all duration-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#C5A57D]/10 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-[#C5A57D]" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-[#0A5F38] mb-1">
            {dashboardData.newCustomers}
          </h3>
          <p className="text-slate-600 text-sm">Total Customers</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          onClick={() => navigate('/sales/estimates?tab=form')}
          className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:border-[#0A5F38]/30 transition-all duration-200 cursor-pointer group"
        >
          <div className="w-12 h-12 bg-[#0A5F38]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0A5F38]/20 transition-colors">
            <FileText className="h-6 w-6 text-[#0A5F38]" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Create Estimate</h3>
          <p className="text-slate-600 text-sm mb-4">Generate detailed customer estimates with professional pricing breakdown</p>
          <button className="w-full bg-[#C5A57D] text-white py-2 px-4 rounded-lg hover:bg-[#C5A57D] transition-colors flex items-center justify-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Create Now</span>
          </button>
        </div>

        <div
          onClick={() => navigate('/sales/invoices?tab=form')}
          className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:border-[#0A5F38]/30 transition-all duration-200 cursor-pointer group"
        >
          <div className="w-12 h-12 bg-[#0A5F38]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0A5F38]/20 transition-colors">
            <Receipt className="h-6 w-6 text-[#0A5F38]" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Create Invoice</h3>
          <p className="text-slate-600 text-sm mb-4">Direct sales invoice for immediate sales transactions</p>
          <button className="w-full bg-[#0A5F38] text-white py-2 px-4 rounded-lg hover:bg-[#0A5F38] transition-colors flex items-center justify-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Create Now</span>
          </button>
        </div>

        <div
          onClick={() => navigate('/sales/closed-orders')}
          className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg hover:border-[#C5A57D]/30 transition-all duration-200 cursor-pointer group"
        >
          <div className="w-12 h-12 bg-[#C5A57D]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#C5A57D]/20 transition-colors">
            <Users className="h-6 w-6 text-[#C5A57D]" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Convert Estimate to Closed Order</h3>
          <p className="text-slate-600 text-sm mb-4">Convert approved estimates into closed orders for further processing</p>
          <button className="w-full bg-[#C5A57D] text-white py-2 px-4 rounded-lg hover:bg-[#C5A57D] transition-colors flex items-center justify-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Convert Now</span>
          </button>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-[#0A5F38]">Recent Transactions</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Invoice</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {dashboardData.recentTransactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-white transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-slate-900">
                      {transaction.invoice_number || `INV-${transaction.id?.slice(-8)}`}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-900">{transaction.customer_name || 'Walk-in Customer'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-600">
                      {new Date(transaction.date).toLocaleDateString('en-IN')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-slate-900">
                      {formatCurrency(transaction.totalAmount || 0)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(transaction.payment_status)}
                      <span className={`text-sm font-medium ${transaction.payment_status === 'paid' || transaction.payment_status === 'completed'
                        ? 'text-green-600'
                        : transaction.payment_status === 'partial'
                          ? 'text-orange-600'
                          : 'text-red-600'
                        }`}>
                        {getStatusText(transaction.payment_status)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModernDashboard;




